/**
 * Orderly logout — prevents the 401 interceptor from double-redirecting
 * while a logout is already in progress.
 */

// Module-level flag intentionally outside React state so axios interceptors
// can read it synchronously without a re-render cycle.
let _isLoggingOut = false;

export function getIsLoggingOut(): boolean {
  return _isLoggingOut;
}

export interface LogoutOptions {
  /** Keys to remove from localStorage before navigating. */
  storageKeys?: string[];
  /** Called after storage is cleared — use to reset React state. */
  onClear?: () => void;
  /** Navigation callback; defaults to window.location.href = '/login'. */
  navigate?: () => void;
}

/**
 * Performs an orderly logout:
 * 1. Sets isLoggingOut flag so in-flight 401 interceptors skip their redirect.
 * 2. Clears localStorage keys.
 * 3. Calls onClear (React state reset).
 * 4. Navigates to /login.
 */
export function performLogout(options?: LogoutOptions): void {
  if (_isLoggingOut) return;
  _isLoggingOut = true;

  (options?.storageKeys ?? []).forEach((key) => localStorage.removeItem(key));
  options?.onClear?.();

  const navigateFn = options?.navigate ?? (() => { window.location.href = '/login'; });
  navigateFn();
}

/** Reset the flag — used in tests and after successful login. */
export function resetLogoutFlag(): void {
  _isLoggingOut = false;
}
